import { useRouter } from '@tanstack/react-router';
import type { MouseEvent, ReactNode } from 'react';
import { RESTRICTED_TRANSITIONS, RESTRICTED_TRANSITION_LABELS, RESTRICTED_TRANSITION_RULES, type RestrictedTransition } from '@nest-recruit/shared';
import { Chip } from '../../../../components/Chip';
import { Switch } from '../../../../components/Switch';
import { peopleText } from './RoleList';
import styles from './RoleAside.module.css';

// Roles A, the right side, in two parts so a phone can put the plain words above the rows and the
// rest below them (the phone drawing).

interface PlainWordsProps {
  readonly changes: readonly string[];
  readonly sentences: readonly string[];
  readonly className?: string;
}

/** "What you changed" and "This role now, in plain words": both live, as the Admin works. */
export function RolePlainWords({ changes, sentences, className }: PlainWordsProps): ReactNode {
  return (
    <aside className={[styles.aside, className].filter(Boolean).join(' ')} aria-label="This role in plain words">
      <section className={styles.section}>
        <h3 className={styles.title}>What you changed</h3>
        {changes.length === 0 ? (
          <p className={styles.muted}>Nothing yet. Your changes show here until you save them.</p>
        ) : (
          <ul className={styles.changes}>
            {changes.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        )}
      </section>

      <section className={styles.section}>
        <h3 className={styles.title}>This role now, in plain words</h3>
        <ul className={styles.sentences}>
          {sentences.map((sentence) => (
            <li key={sentence}>{sentence}</li>
          ))}
        </ul>
      </section>
    </aside>
  );
}

interface RoleExtrasProps {
  readonly roleId: string;
  readonly transitions: readonly RestrictedTransition[];
  readonly allowed: Readonly<Record<string, boolean>>;
  readonly onTransitionChange: (key: RestrictedTransition, isAllowed: boolean) => void;
  readonly assignedUserCount: number;
  readonly hiddenCount: number;
  readonly showHidden: boolean;
  readonly onShowHiddenChange: (show: boolean) => void;
  readonly canEdit: boolean;
  readonly className?: string;
}

/** The special actions, who has the role, and the rows hidden because their part is off. */
export function RoleExtras({
  roleId,
  transitions,
  allowed,
  onTransitionChange,
  assignedUserCount,
  hiddenCount,
  showHidden,
  onShowHiddenChange,
  canEdit,
  className,
}: RoleExtrasProps): ReactNode {
  const allowedCount = transitions.filter((key) => allowed[key]).length;

  return (
    <div className={[styles.aside, className].filter(Boolean).join(' ')}>
      <section className={styles.section}>
        <h3 className={styles.title}>
          Special actions
          <span className={styles.count}>
            {allowedCount} of {transitions.length}
          </span>
        </h3>
        <ul className={styles.actions}>
          {transitions.map((key) => (
            <li key={key} className={styles.action}>
              <span className={styles.actionText}>
                <span className={styles.actionLabel}>{RESTRICTED_TRANSITION_LABELS[key]}</span>
                {RESTRICTED_TRANSITION_RULES[key].requiresReason === true && (
                  <Chip tone="attention" plain>
                    Reason required
                  </Chip>
                )}
              </span>
              <Switch
                ariaLabel={RESTRICTED_TRANSITION_LABELS[key]}
                checked={allowed[key] ?? false}
                disabled={!canEdit}
                onChange={(checked) => onTransitionChange(key, checked)}
              />
            </li>
          ))}
        </ul>
        {transitions.length < RESTRICTED_TRANSITIONS.length && !showHidden && (
          <p className={styles.muted}>Special actions of switched-off parts are hidden.</p>
        )}
      </section>

      <section className={styles.section}>
        <h3 className={styles.title}>
          {assignedUserCount === 0 ? 'Nobody has it yet' : `${peopleText(assignedUserCount)} ${assignedUserCount === 1 ? 'has' : 'have'} it`}
          {assignedUserCount > 0 && <SeeThem roleId={roleId} />}
        </h3>
      </section>

      {hiddenCount > 0 && (
        <section className={styles.hint}>
          <p className={styles.hintText}>
            {showHidden
              ? `Showing ${hiddenCount} things whose parts are switched off. They apply once the part is switched on.`
              : `${hiddenCount} things are hidden because their parts are switched off.`}
          </p>
          <Switch label="Show them anyway" checked={showHidden} onChange={onShowHiddenChange} />
        </section>
      )}
    </div>
  );
}

/** "See them": People filtered by this role. A real link, so it opens in a new tab too (06-qol §8). */
function SeeThem({ roleId }: { readonly roleId: string }): ReactNode {
  const router = useRouter();
  const href = `/admin/users?roleId=${encodeURIComponent(roleId)}`;
  const follow = (event: MouseEvent<HTMLAnchorElement>): void => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    router.history.push(href);
  };
  return (
    <>
      <span aria-hidden> · </span>
      <a className={styles.link} href={href} onClick={follow}>
        See them
      </a>
    </>
  );
}
